import { Intent, Tag } from "@blueprintjs/core";
import classnames from "classnames";
import React from "react";
import {
    getScoreBucket,
    ScoreBucketType,
    SCORE_BUCKET_LABEL
} from "types/queueProjectRelatednessType";
import "./ScoreBucketTag.scss";

/**
 * Visual styling per bucket. Intensity progresses with severity:
 * gray → blue → orange → red so a quick scan tells the user where the
 * heaviest competitors sit. `minimal` is used for the lower tiers so
 * they don't dominate the visual weight.
 */
const SCORE_BUCKET_INTENT: { readonly [K in ScoreBucketType]: Intent } = {
    negligible: Intent.NONE,
    low: Intent.PRIMARY,
    medium: Intent.WARNING,
    high: Intent.WARNING,
    dominant: Intent.DANGER
};

const SCORE_BUCKET_MINIMAL: { readonly [K in ScoreBucketType]: boolean } = {
    negligible: true,
    low: true,
    medium: true,
    high: false,
    dominant: false
};

/**
 * Renders a queue project's relatedness score as a qualitative impact
 * tag. Shared by the related-projects sidebar and the per-constraint
 * expand panel so impact reads identically across the bus page.
 *
 * `notInScope` renders the dashed-outline variant: the project is not
 * dispatched in the currently selected scope, so its generation is not
 * reflected in the constraint loadings on the page. The bucket keeps its
 * intent color so ranking stays scannable; the dashed outline is forced
 * minimal so the intent reads through the text rather than a solid fill.
 */
export const ScoreBucketTag: React.FC<{
    readonly score: number | undefined;
    readonly notInScope?: boolean;
}> = ({ score, notInScope }) => {
    if (score === undefined) {
        return <span className="bp5-text-muted">—</span>;
    }
    const bucket = getScoreBucket(score);
    return (
        <Tag
            intent={SCORE_BUCKET_INTENT[bucket]}
            minimal={notInScope === true || SCORE_BUCKET_MINIMAL[bucket]}
            className={classnames({
                "ScoreBucketTag-not-in-scope": notInScope === true
            })}
        >
            {SCORE_BUCKET_LABEL[bucket]}
        </Tag>
    );
};

/**
 * The sample tag for legends explaining the dashed styling, kept here so
 * the dashed class stays private to this component. Reads "Impact"
 * (rather than any one bucket label) since the dashed treatment applies
 * at every impact level.
 */
export const NotInScopeLegendTag: React.FC = () => (
    <Tag
        minimal
        className={classnames(
            "ScoreBucketTag-not-in-scope",
            "ScoreBucketTag-legend-sample"
        )}
    >
        Impact
    </Tag>
);
